// The linked-sessions-by-patient-profile read and its two invalidation
// commands, moved from `src/hooks/query/useQueryLinkedSessionsByProfileId.ts`
// (now a delegate) with the session-header modal cores. Bodies are
// byte-identical apart from the two prescribed crossings below.
//
// ### The cohort trio
//
// The composed read branches on `useRBACPermissionCheck().isReviewingOtherUser`
// and the team-member arm keys on the team id + selected member. All three
// arrive through `useSessionReviewCohort` — ruling 32's required
// `useSessionAccessContext` member, read once. See that module for the seam,
// why its argument is empty, and why it costs no new request here.
//
// ### The module-scope `reactQueryClient`
//
// Both invalidation commands are free functions, so they addressed the app's
// client singleton by importing it. They now read
// `getSessionDataConfig().getQueryClient()` — the `invalidateSessionById`
// precedent in this package, and a getter rather than an instance so wiring
// order cannot race client construction.
//
// ### The keys are tuples, not strings
//
// The team-member arm is four POSITIONAL elements and the invalidator rebuilds
// it from caller-supplied values, so reader and invalidator must agree element
// for element. Both go through `linkedSessionsByProfileKeys`
// (`@heidi/session/contracts`) and `keys.frozen.test.ts` pins the emitted
// arrays. `teamId` / `memberId` stay UNCOERCED for the same reason — an
// unresolved id widened to `''` is a third spelling nobody addresses.
//
// COLAB-1: the flag-gated v2.2 cutover is confined to the DRAWER. Two
// drawer-specific hooks (`useQueryDrawerOwnSessionsByProfileId` and its
// composed sibling `useQueryDrawerLinkedSessions`) route the drawer's own
// arm through `release-scribe-shared-patient-directory` to v2.2 `scope=all`,
// while the shared reader `useQueryLinkedSessionsByProfileId` and its
// non-drawer consumers (linked-session list, dropdown, past-sessions button,
// auto-include, session-toggle, unlink-all modal, patient-profile mutation
// cache walk) stay on v2 unconditionally — those surfaces are out of
// COLAB-1 scope and their UIs assume own-only session lists. The drawer key
// extends `.own(id)` with `{ drawer: true, flag_on }` so drawer and shared
// branches live in distinct cache entries; invalidators using the `.own(id)`
// prefix match both.

import { skipToken, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  SessionScope,
  SessionSortBy,
  Shared__enums__enums__SortOrder as SortOrder,
} from '@heidi/schema/ml-scribe';

import {
  GET_LINKED_SESSIONS_BY_PROFILE_ID,
  GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID,
  linkedSessionsByProfileKeys,
  type SessionListItem,
  type SessionListResponse,
  type SimpleSessionResponse,
} from '../../contracts';
import {
  getSessionsByPatientProfileId,
  getSessionsByPatientProfileIdV2_2,
  getSessionsListV2,
  getTeamMemberSessionsByPatientProfileId,
} from '../api/list';
import { getSessionDataConfig } from '../config';
import { useSessionReviewCohort } from '../sessionReviewCohort';

export function useQueryLinkedSessionsByProfileId(patientProfileId: string) {
  const { isReviewingOtherUser } = useSessionReviewCohort();
  const ownSessionsQuery = useQueryLinkedOwnSessionsByProfileId(patientProfileId);
  const teamMemberSessionsQuery =
    useQueryTeamMemberSessionsByPatientProfileId(patientProfileId);

  if (isReviewingOtherUser) {
    return teamMemberSessionsQuery;
  }
  return ownSessionsQuery;
}

export function useQueryLinkedOwnSessionsByProfileId(patientProfileId: string) {
  return useQuery({
    queryKey: linkedSessionsByProfileKeys.own(patientProfileId),
    enabled: !!patientProfileId,
    queryFn: patientProfileId
      ? async () => {
          const { data } = await getSessionsByPatientProfileId(patientProfileId);
          return data;
        }
      : skipToken,
  });
}

// Module-scope fallback so the injected flag hook is called at STABLE
// identity per app instance — a per-render `?? () => false` would change
// identity every render, which the flag-hook implementation may or may not
// tolerate depending on its own hook internals. This is a plain constant.
const noSharedDirectoryFlag = (): boolean => false;

/**
 * The drawer's own-arm reader — routes through v2.2 `scope=all` under
 * `release-scribe-shared-patient-directory`, otherwise v2 (byte-identical to
 * `useQueryLinkedOwnSessionsByProfileId`).
 *
 * Kept module-internal: only the composed `useQueryDrawerLinkedSessions`
 * consumes it. Every other linked-sessions reader in the app stays on the
 * shared hook by design (COLAB-1 non-goal), so widening the drawer must not
 * offer this as a public entry point.
 *
 * Cache key extends the shared `.own(id)` prefix with `{ drawer: true,
 * flag_on }`, so the drawer's two branches (v2 and v2.2) AND the shared
 * reader's v2 entry live in three distinct entries under one invalidator
 * prefix. `invalidateQueries({ queryKey: linkedSessionsByProfileKeys.own(id)
 * })` prefix-matches all three, so link/unlink/delete invalidators stay
 * correct without a per-branch fork.
 *
 * Reviewer gate: `enabled` conditions on `!isReviewingOtherUser` so the
 * drawer's composed hook (which returns the team-member query in reviewer
 * mode) never fires this reader wastefully or emits a truncation/403 RUM
 * signal for a query the drawer never renders.
 */
function useQueryDrawerOwnSessionsByProfileId(patientProfileId: string) {
  const { isReviewingOtherUser } = useSessionReviewCohort();
  const useFlag =
    getSessionDataConfig().useIsSharedPatientDirectoryEnabled ?? noSharedDirectoryFlag;
  const isSharedDirectoryEnabled = useFlag();
  return useQuery({
    queryKey: [
      ...linkedSessionsByProfileKeys.own(patientProfileId),
      { drawer: true, flag_on: isSharedDirectoryEnabled },
    ],
    enabled: !!patientProfileId && !isReviewingOtherUser,
    queryFn: patientProfileId
      ? async () => {
          if (isSharedDirectoryEnabled) {
            const { data, next_cursor } =
              await getSessionsByPatientProfileIdV2_2(patientProfileId);
            return { ...data, next_cursor };
          }
          const { data } = await getSessionsByPatientProfileId(patientProfileId);
          return data;
        }
      : skipToken,
  });
}

/**
 * The DRAWER's composed reader — cohort-aware like the shared
 * `useQueryLinkedSessionsByProfileId`, but its own arm routes to v2.2 under
 * the flag. Only the drawer imports this; every other consumer stays on the
 * shared reader.
 */
export function useQueryDrawerLinkedSessions(patientProfileId: string) {
  const { isReviewingOtherUser } = useSessionReviewCohort();
  const drawerOwnQuery = useQueryDrawerOwnSessionsByProfileId(patientProfileId);
  const teamMemberSessionsQuery =
    useQueryTeamMemberSessionsByPatientProfileId(patientProfileId);

  const query = isReviewingOtherUser ? teamMemberSessionsQuery : drawerOwnQuery;

  // Expose a gated next-page fetcher so the component never touches the raw
  // transport directly. Gates are baked in here: reviewer arm returns null,
  // preventing a raw scope=all fetch in reviewer mode.
  const fetchNextPage = async (cursor: string) => {
    if (isReviewingOtherUser) return null;
    return getSessionsByPatientProfileIdV2_2(patientProfileId, cursor);
  };

  return { ...query, isReviewingOtherUser, fetchNextPage };
}

const PAGED_LINKED_SESSIONS_PAGE_SIZE = 10;

type PagedLinkedSessionFields =
  | 'session_id'
  | 'session_name'
  | 'created_at'
  | 'created_time_local';

export type PagedLinkedSession =
  | Pick<SessionListItem, PagedLinkedSessionFields>
  | Pick<SimpleSessionResponse, PagedLinkedSessionFields>;

/**
 * The caller's own sessions for one patient profile, newest first, one v2.2
 * `scope=me` cursor page at a time. Keyed under `linkedSessionsByProfileKeys.own`
 * so the link/unlink/delete invalidators refresh it with the other own-arm
 * entries.
 *
 * A reviewer reads the team-member arm instead, which is unpaginated v2, so
 * `hasNextPage` is always false there.
 */
export function useQueryPagedLinkedSessionsByProfileId(patientProfileId: string) {
  const { isReviewingOtherUser } = useSessionReviewCohort();
  const ownPages = useInfiniteQuery({
    queryKey: [...linkedSessionsByProfileKeys.own(patientProfileId), { paged: true }],
    enabled: !!patientProfileId && !isReviewingOtherUser,
    queryFn: async ({ pageParam }): Promise<SessionListResponse> => {
      const { data } = await getSessionsListV2({
        scope: SessionScope.me,
        patient_profile_ids: [patientProfileId],
        page_size: PAGED_LINKED_SESSIONS_PAGE_SIZE,
        sort_by: SessionSortBy.created_at,
        sort_order: SortOrder.DESC,
        ...(pageParam != null ? { cursor: pageParam } : {}),
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
  const teamMemberSessionsQuery =
    useQueryTeamMemberSessionsByPatientProfileId(patientProfileId);

  const ownSessions = useMemo(
    () => ownPages.data?.pages.flatMap((page) => page.sessions ?? []),
    [ownPages.data]
  );

  if (isReviewingOtherUser) {
    const sessions: readonly PagedLinkedSession[] | undefined =
      teamMemberSessionsQuery.data?.sessions;
    return {
      sessions,
      isLoading: teamMemberSessionsQuery.isLoading,
      isError: teamMemberSessionsQuery.isError,
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: ownPages.fetchNextPage,
    };
  }
  const sessions: readonly PagedLinkedSession[] | undefined = ownSessions;
  return {
    sessions,
    // A failed next page keeps the loaded rows; `hasNextPage` stays true so it can be retried.
    isLoading: ownPages.isLoading,
    isError: ownPages.isError && !ownPages.data,
    hasNextPage: ownPages.hasNextPage,
    isFetchingNextPage: ownPages.isFetchingNextPage,
    fetchNextPage: ownPages.fetchNextPage,
  };
}

export function useQueryTeamMemberSessionsByPatientProfileId(patientProfileId: string) {
  const {
    isReviewingOtherUser,
    teamId,
    memberId: selectedReviewMember,
  } = useSessionReviewCohort();
  return useQuery({
    queryKey: linkedSessionsByProfileKeys.teamMember(
      patientProfileId,
      teamId,
      selectedReviewMember
    ),
    enabled: !!patientProfileId && isReviewingOtherUser,
    queryFn:
      patientProfileId && teamId && selectedReviewMember
        ? async () => {
            const { data } = await getTeamMemberSessionsByPatientProfileId(
              teamId,
              selectedReviewMember,
              patientProfileId
            );
            return data;
          }
        : skipToken,
  });
}

export function invalidateQueryLinkedSessionsByProfileId({
  patientProfileId,
  isReviewingOtherUser = false,
  teamId,
  selectedReviewMember,
}: {
  patientProfileId: string;
  isReviewingOtherUser?: boolean;
  teamId?: string;
  selectedReviewMember?: string;
}) {
  const queryClient = getSessionDataConfig().getQueryClient();

  if (isReviewingOtherUser && teamId && selectedReviewMember) {
    return queryClient.invalidateQueries({
      queryKey: linkedSessionsByProfileKeys.teamMember(
        patientProfileId,
        teamId,
        selectedReviewMember
      ),
    });
  }

  return queryClient.invalidateQueries({
    queryKey: linkedSessionsByProfileKeys.own(patientProfileId),
  });
}

export async function invalidateQueryLinkedSessions() {
  const queryClient = getSessionDataConfig().getQueryClient();
  // Invalidate all queries that have GET_LINKED_SESSIONS_BY_PROFILE_ID in their query key
  await queryClient.invalidateQueries({
    predicate: (query) => query.queryKey.includes(GET_LINKED_SESSIONS_BY_PROFILE_ID),
  });
  // Invalidate all queries that have GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID in their query key
  await queryClient.invalidateQueries({
    predicate: (query) => query.queryKey.includes(GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID),
  });
}
